import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, TextField,
  LinearProgress, Chip, CircularProgress, Snackbar, Alert,
  InputAdornment, Tooltip, Pagination, Grid as MuiGrid
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { 
  Upload, Camera, FileText, Image, CheckCircle, AlertCircle, X, DollarSign, Tag, Search, Download, Send, HelpCircle
} from 'lucide-react';
import ReceiptService from '../../services/AGTUps-product-services/receiptService';
import TransactionService from '../../services/AGTUps-product-services/transactionService';

/* ===== PALETA DE COLORES ===== */
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

/* ===== CONTENEDOR SCROLLABLE ===== */
const QuickUploadContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: 'calc(100vh - 180px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  },
}));

/* ===== STYLED PAPER UNIFICADO ===== */
const StyledPaperUnified = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: 'none',
  };
});

/* ===== UPLOAD ZONE ===== */
const UploadZone = styled(Box)(({ theme, isDragging }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    padding: theme.spacing(6),
    borderRadius: theme.spacing(2),
    border: `2px dashed ${isDragging
      ? (isWallpaperMode ? '#FFFFFF' : '#0385FF')
      : theme.palette.mode === 'dark'
        ? appleColors.dark.system.grey3
        : appleColors.light.system.grey3}`,
    backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    textAlign: 'center',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    },
  };
});

/* ===== PREVIEW CARD ===== */
const PreviewCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    marginBottom: theme.spacing(2),
  };
});

/* ===== STYLED TEXTFIELD PARA SEARCH ===== */
const StyledTextField = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.25)'
        : (theme.palette.mode === 'dark'
          ? appleColors.dark.ui.base
          : appleColors.light.ui.base),
      backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
      '& fieldset': {
        border: 'none',
      },
    },
  };
});

/* ===================== COMPONENTES DE MODALES ===================== */

// Camera Upload Dialog
const CameraUploadDialog = ({ open, onClose, videoRef, onCapture }) => {
  const theme = useTheme();
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: '1px solid',
          borderColor: theme.palette.mode === 'light' 
            ? 'rgba(0, 0, 0, 0.12)' 
            : 'rgba(255, 255, 255, 0.12)',
        }
      }}
    >
      <DialogTitle 
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Scan Receipt</Typography>
        <Tooltip title="Center the receipt in the frame">
          <IconButton size="small">
            <HelpCircle size={20} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
          Position the receipt within the frame and ensure good lighting for best results.
        </Alert>
        <Box sx={{ position: 'relative' }}>
          <video
            ref={videoRef}
            autoPlay
            style={{ 
              width: '100%', 
              borderRadius: '12px',
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: '2px dashed',
              borderColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
              borderRadius: '12px'
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onCapture}
          startIcon={<Camera size={20} />}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : '#0385FF',
            color: '#FFFFFF',
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(128, 128, 128, 0.4)'
                : '#0266cc',
              boxShadow: 'none'
            }
          }}
        >
          Capture
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Preview Dialog
const PreviewDialog = ({ open, onClose, upload, onConfirm, confirming }) => {
  const theme = useTheme();
  if (!upload) return null;

  const { receipt, transactionData } = upload;
  const { fileName } = receipt;
  
  const invoiceRef = 
    transactionData.description &&
    typeof transactionData.description === 'object' &&
    typeof transactionData.description.invoice_reference === 'string'
      ? transactionData.description.invoice_reference
      : null;
  
  const concatenatedMessage = `
Receipt: ${fileName}

This is the extracted analysis from the receipt:

Transaction Details:
- Title: ${transactionData.title || 'N/A'}
- Date: ${transactionData.date || 'N/A'}
- Amount: ${transactionData.amount || 'N/A'}
- Category: ${transactionData.category || 'N/A'}
- Type: ${transactionData.type || 'N/A'}
${invoiceRef ? `- Invoice Reference: ${invoiceRef}` : ''}
  `;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: '1px solid',
          borderColor: theme.palette.mode === 'light' 
            ? 'rgba(0, 0, 0, 0.12)' 
            : 'rgba(255, 255, 255, 0.12)',
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {fileName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
          This is the extracted analysis from the receipt:
        </Alert>
        <Typography 
          variant="body1" 
          sx={{ 
            whiteSpace: 'pre-wrap', 
            fontFamily: 'monospace', 
            backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f7f7f7', 
            p: 2, 
            borderRadius: '8px' 
          }}
        >
          {concatenatedMessage}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          startIcon={confirming ? <CircularProgress size={20} /> : <Send size={20} />}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : '#0385FF',
            color: '#FFFFFF',
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(128, 128, 128, 0.4)'
                : '#0266cc',
              boxShadow: 'none'
            }
          }}
          disabled={confirming}
        >
          {confirming ? 'Saving...' : 'Confirm & Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Help Dialog
const UploadHelpDialog = ({ open, onClose }) => {
  const theme = useTheme();
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: '1px solid',
          borderColor: theme.palette.mode === 'light' 
            ? 'rgba(0, 0, 0, 0.12)' 
            : 'rgba(255, 255, 255, 0.12)',
        }
      }}
    >
      <DialogTitle 
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Upload Tips
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          <MuiGrid item xs={12}>
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                For Best Results
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Camera
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Ensure good lighting when scanning
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Image
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Keep the receipt flat and centered
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AlertCircle
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Avoid shadows and glare
                  </Typography>
                </Box>
              </Box>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={12}>
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Supported Formats
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FileText
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Documents: PDF files
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Image
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Images: JPG, PNG, HEIC
                  </Typography>
                </Box>
              </Box>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={12}>
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.02)'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Auto-Detection Features
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Vendor name and details
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DollarSign
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Transaction amount and date
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Tag
                    size={20}
                    color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'}
                  />
                  <Typography variant="body2">
                    Item-level details and categories
                  </Typography>
                </Box>
              </Box>
            </Box>
          </MuiGrid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ===================== COMPONENTE PRINCIPAL ===================== */

const QuickUpload = () => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // Archivos pendientes de subir
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [showProcessingStatus, setShowProcessingStatus] = useState(false);
  const [openHelpDialog, setOpenHelpDialog] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]); // Datos reales de recibos subidos
  const [confirming, setConfirming] = useState(false);

  // Estados para búsqueda y paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Detectar si se está en un dispositivo móvil
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Debug: Verificar el estado de selectedUpload
  useEffect(() => {
    console.log('selectedUpload:', selectedUpload);
  }, [selectedUpload]);

  // Cargar los recibos recientes desde el backend al iniciar y cada vez que cambie la página o el término de búsqueda
  useEffect(() => {
    const fetchRecentUploads = async () => {
      try {
        const response = await ReceiptService.getRecentUploads(currentPage, 10, searchTerm);
        if (response?.data) {
          setRecentUploads(response.data.receipts);
          setTotalPages(response.data.totalPages);
        }
      } catch (error) {
        console.error('Error fetching recent uploads:', error);
      }
    };
    fetchRecentUploads();
  }, [currentPage, searchTerm]);

  // Manejo del drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  // Manejo de la subida de archivos
  const handleFiles = async (files) => {
    for (const file of files) {
      const fileId = Date.now() + Math.random();
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      const newFile = { id: fileId, file, preview, status: 'uploading' };
      setSelectedFiles((prev) => [...prev, newFile]);
  
      try {
        const uploadResponse = await ReceiptService.uploadReceipt(file);
        if (uploadResponse?.data?.transactionData) {
          const processedReceipt = uploadResponse.data; // { receipt, transactionData }
          console.log('Processed Receipt:', processedReceipt);
          setRecentUploads((prev) => [processedReceipt.receipt, ...prev]);
          newFile.status = 'complete';
          newFile.processedData = processedReceipt;
          setSelectedUpload(processedReceipt);
          setOpenPreview(true);
        }
  
        setShowProcessingStatus(true);
      } catch (error) {
        console.error('Error uploading file:', error);
        newFile.status = 'error';
        newFile.errorMessage = error.message;
      } finally {
        setSelectedFiles((prev) =>
          prev.map((f) => (f.id === fileId ? newFile : f))
        );
      }
    }
  };  

  // Funciones de cámara
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoRef.current.srcObject = stream;
      setIsCameraActive(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
    }
  };
  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
    setIsCameraActive(false);
  };
  const captureImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], `receipt_${Date.now()}.jpg`, { type: 'image/jpeg' });
      handleFiles([file]);
    }, 'image/jpeg');
    stopCamera();
  };

  // Función para confirmar la transacción usando el servicio
  const handleConfirm = async () => {
    if (!selectedUpload?.transactionData) {
      console.error('No transaction data available to confirm');
      return;
    }
    setConfirming(true);
    try {
      console.log('Confirming transaction with data:', selectedUpload.transactionData);
      const response = await TransactionService.confirmTransaction(selectedUpload.transactionData);
      console.log('Transaction confirmed:', response);
      setOpenPreview(false);
    } catch (error) {
      console.error('Error confirming transaction:', error);
    } finally {
      setConfirming(false);
    }
  };

  // Al hacer clic en el área de subida, se adapta según dispositivo
  const handleUploadZoneClick = () => {
    if (isMobile) {
      const useCamera = window.confirm("Do you want to take a photo using your camera? Press OK for camera, Cancel to select a file.");
      if (useCamera) {
        startCamera();
      } else {
        fileInputRef.current?.click();
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  return (
    <QuickUploadContainer>
      {/* Quick Upload Section */}
      <StyledPaperUnified>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Quick Upload
        </Typography>
        <UploadZone
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={handleUploadZoneClick}
        >
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            multiple
            accept="image/*,.pdf"
            onChange={(e) => handleFiles(Array.from(e.target.files))}
          />
          <Upload size={48} style={{ margin: '0 auto 16px', color: alpha(theme.palette.text.secondary, 0.7) }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Drop files here or click to upload
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supports images and PDF files
          </Typography>
        </UploadZone>
      </StyledPaperUnified>

      {/* Lista de archivos pendientes */}
      {selectedFiles.length > 0 && (
        <StyledPaperUnified sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Selected Files
          </Typography>
          <Grid container spacing={2}>
            {selectedFiles.map((file) => (
              <Grid item xs={12} key={file.id}>
                <PreviewCard>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 3 }}>
                    {file.preview ? (
                      <img
                        src={file.preview}
                        alt="preview"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                      />
                    ) : (
                      <FileText size={48} style={{ color: alpha(theme.palette.text.secondary, 0.7) }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" noWrap>
                        {file.file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      {file.status === 'complete' ? (
                        <Chip size="small" icon={<CheckCircle size={14} />} label="Processed" color="success" />
                      ) : file.status === 'uploading' ? (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress variant="indeterminate" />
                        </Box>
                      ) : file.status === 'error' ? (
                        <Alert severity="error" variant="filled" sx={{ mt: 1, borderRadius: '12px' }}>
                          Error uploading
                        </Alert>
                      ) : null}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedFiles((prev) => prev.filter((f) => f.id !== file.id));
                        if (file.preview) URL.revokeObjectURL(file.preview);
                      }}
                    >
                      <X size={16} />
                    </IconButton>
                  </Box>
                </PreviewCard>
              </Grid>
            ))}
          </Grid>
        </StyledPaperUnified>
      )}

      {/* Recent Uploads Section */}
      <StyledPaperUnified sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Recent Uploads
        </Typography>
        {/* Search Field */}
        <StyledTextField
          variant="outlined"
          placeholder="Search receipts..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />
        {recentUploads.length > 0 ? (
          recentUploads.map((upload) => (
            <PreviewCard key={upload.receiptId}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 3 }}>
                  {upload.fileType === 'image' ? (
                    <Image
                      size={24}
                      style={{
                        color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
                      }}
                    />
                  ) : (
                    <FileText size={24} style={{ color: '#7c3aed' }} />
                  )}
                  <Box>
                    <Typography variant="subtitle2">{upload.fileName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {upload.uploadDate}
                    </Typography>
                    {upload.status === 'processed' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <DollarSign size={14} style={{ marginRight: 4 }} />
                        <Typography variant="subtitle2" color="success.main">
                          ${upload.amount}
                        </Typography>
                        <Chip size="small" label={upload.category} />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Se elimina el botón de preview, se deja solo el de descarga */}
                  <IconButton size="small" onClick={() => window.open(upload.fileUrl, '_blank')}>
                    <Download size={16} />
                  </IconButton>
                </Box>
              </Box>
            </PreviewCard>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No receipts found.
          </Typography>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={(e, page) => setCurrentPage(page)} 
              color="primary"
            />
          </Box>
        )}
      </StyledPaperUnified>

      {/* Diálogos */}
      <CameraUploadDialog
        open={isCameraActive}
        onClose={stopCamera}
        videoRef={videoRef}
        onCapture={captureImage}
      />
      <PreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        upload={selectedUpload}
        onConfirm={handleConfirm}
        confirming={confirming}
      />
      <UploadHelpDialog
        open={openHelpDialog}
        onClose={() => setOpenHelpDialog(false)}
      />
      <Snackbar
        open={showProcessingStatus}
        autoHideDuration={6000}
        onClose={() => setShowProcessingStatus(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowProcessingStatus(false)}
          severity="success"
          elevation={6}
          variant="filled"
          sx={{ borderRadius: '12px' }}
        >
          Receipt processed successfully!
        </Alert>
      </Snackbar>
    </QuickUploadContainer>
  );
};

export default QuickUpload;
